import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, LogOut, Home, Download ,ArrowLeftCircle} from "lucide-react";
import { logout } from '../../../services/auth';
import { getProposalMIS_Practo } from "../../../services/api";

import logo from '../../../../src/assets/img/TravelAssist_practo.webp';
import '../UpdatePolicy/generatecoi.css';

interface Proposal {
    Practo_proposal_id?: number;   
    Practo_inv_number?: string; // Corresponds to Practo_inv_number
    Planname?: string;
    Nameofthepassenger?: string;
    passenger_Mobileno?: string;
    Policy_Generation_Date?: string; // Corresponds to CreateDate
    Assiatance_charges_PreTaxAmount?: string | number; // Corresponds to PremiumAmount
    Assiatance_charges_PostTaxAmount?: string | number; // Corresponds to Selected_PremiumAmount
    AgentId?: number;
    Agent_Mobileno?: string;
    AgentName?: string;
    Selected_Payment_Mode?: string;
    PaymentType?: string; // 'Wallet' in SP
    Discount?: string; // Corresponds to Payout
    Fullpay_Discount_amount_to_be_paid?: string | number; // Corresponds to Selected_PremiumAmount
    Paymentreceived?: string;
    UId?: string;
    Payment_Status?: string; 
    practo_fees?:string;
}

interface ApiResponse {
    Status: string;
    MasterData?: {
        proposals?: Proposal[];
    }| null;
}

const MIS_Proposal_Practo: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state || {};

    const [empId, setEmpId] = useState<string>(state.empid || '');
    const [agentId, setAgentId] = useState<string>(state.agent || state.agentData?.AgentId || '');

    const [adminId, setAdminId] = useState<string>(state.adminId || '');
    const [userType, setUserType] = useState<string>(state.userType || '');

    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [proposal, setProposals] = useState<Proposal[]>([]);

    const [paymentFilter, setPaymentFilter] = useState<'Discount' | 'Upfront'>('Discount');

    useEffect(() => {
        // Set dates: 7 days ago to today
        const today = new Date();
        //  const sevenDaysAgo = new Date(today);
        // sevenDaysAgo.setDate(today.getDate() - 14);
        //   const start = sevenDaysAgo.toISOString().split('T')[0];
        //   const end = today.toISOString().split('T')[0];

        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

        const formatDate1 = (date: Date) => {
            return date.toLocaleDateString("en-CA", {
                timeZone: "Asia/Kolkata" // Use IST
            });
        };
        const start = formatDate1(startOfMonth);
        const end = formatDate1(today);

        setStartDate(start);
        setEndDate(end);

        // Auto search based on user type
        if (userType === 'Employee' && empId) {
            searchData(start, end, empId, '');
        } else if (userType === 'Agent' && agentId) {
            searchData(start, end, '', agentId);
        } else if (userType === 'Admin') {
            searchData(start, end, '', '');
        }
    }, [empId, agentId, userType]);

    const formatDate = (dateStr?: string): string => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB');
    };

    const calculateUpfrontAmount = (proposal: Proposal): number => {
        const preTax = parseFloat(String(proposal.Assiatance_charges_PreTaxAmount)) || 0;
        const postTax = parseFloat(String(proposal.Assiatance_charges_PostTaxAmount)) || 0;
        const discountComm = parseFloat(String(proposal.Discount)) || 0;
        const amountPaid = parseFloat(String(proposal.Fullpay_Discount_amount_to_be_paid)) || 0;

        const upfront_amt_filter = postTax * (discountComm / 100);

        // Applying the formula provided by the user
        const upfrontAmount = amountPaid + (upfront_amt_filter * 0.02);

        return Math.round(upfrontAmount);
    };

  

    const handleSearch = (): void => {
        if (userType === 'Employee') {
            searchData(startDate, endDate, empId, '');
        } else if (userType === 'Agent') {
            searchData(startDate, endDate, '', agentId);
        } else {
            searchData(startDate, endDate, '', '');
        }
    };

    const searchData = async (fromDate: string, toDate: string, employeeId: string, agentId: string): Promise<void> => {
        if (!fromDate || !toDate) {
            setError('Please fill all fields');
            return;
        }

        // Validation based on user type
        if (userType === 'Employee' && !employeeId) {
            setError('Employee ID is required');
            return;
        }

        if (userType === 'Agent' && !agentId) {
            setError('Agent ID is required');
            return;
        }

        setLoading(true);
        setError('');

        try {
            let response: ApiResponse;

            if (userType === 'Employee') {
                response = await getProposalMIS_Practo(fromDate, toDate, employeeId, '');
            } else if (userType === 'Agent') {
                response = await getProposalMIS_Practo(fromDate, toDate, '', agentId);
            } else {
                // Admin or other user types - fetch all data
                response = await getProposalMIS_Practo(fromDate, toDate, '', '');
            }

            if (response.Status === 'Success' && response.MasterData?.proposals && response.MasterData.proposals.length > 0) {
                setProposals(response.MasterData.proposals);
              
                 const firstProposal = response.MasterData.proposals[0];
                 const paymentModeFromAPI = firstProposal.Selected_Payment_Mode;
                  if (paymentModeFromAPI === 'Upfront' || paymentModeFromAPI === 'Discount') {
                    // If it's valid, set it
                    setPaymentFilter(paymentModeFromAPI);
                } else {
                    // Otherwise, fall back to a default value to ensure type safety
                    setPaymentFilter('Discount');
                }
              
              
                setError('');
            } else {
                setProposals([]);
                setError('No data found');
            }
        } catch (err) {
            setProposals([]);
            setError('Error loading data');
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredProposals = proposal.filter(proposal => {
        if (paymentFilter === 'Discount') {
            return proposal.Selected_Payment_Mode !== 'Upfront';
        }
        if (paymentFilter === 'Upfront') {
            return proposal.Selected_Payment_Mode === 'Upfront';
        }
        return true; // Fallback
    });

    // CSV Download Function
    const downloadCSV = (): void => {
        if (proposal.length === 0) {
            alert('No data available to download');
            return;
        }

        // Define CSV headers
        const headers = [
            'Sr.No',
            'Practo Proposal ID',
            'Practo Invoice No',
            'Plan Name',
            'Name of the Passenger',
            'Passenger Mobile No',         
            'Policy Subscription Date',            
            'Pre Tax Amount',
            'Post Tax Amount',
            'Agent ID', // Using AgentId from the data
            'Agent Name',
            'Agent Mobile No',
            'Selected Payment Mode',
            'Payment Type',
            'Discount/Comm%',
             paymentFilter === 'Upfront' ? 'Upfront Amount Paid' : 'Full Pay/Discount Amount Paid',
            'Payment Received',
             'Practo Fees'
        ];

        // Convert data to CSV format
          const csvData = proposal.map((item:Proposal, index: number) => {
            const amountCell = paymentFilter === 'Upfront'
                ? calculateUpfrontAmount(item)
                : item.Fullpay_Discount_amount_to_be_paid || '';
            return [
                index + 1,
                item.Practo_proposal_id || '',
                item.Practo_inv_number || '',
                item.Planname || '',
                item.Nameofthepassenger || '',
                item.passenger_Mobileno || '',
              
                formatDate(item.Policy_Generation_Date),
                item.Assiatance_charges_PreTaxAmount || '',
                item.Assiatance_charges_PostTaxAmount || '',
                item.AgentId || '',
                item.AgentName || '',
                item.Agent_Mobileno || '',
                item.Selected_Payment_Mode || '',
                item.PaymentType || '',
                item.Discount || '',
                amountCell,
                item.Paymentreceived || '',
                item.practo_fees
            ]
        });

        // Combine headers and data
        const allData = [headers, ...csvData];

        // Convert to CSV string
         const csvContent = allData.map(row =>
            row.map(field => {
                const stringField = String(field);
                if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
                    // Enclose in double quotes and escape existing double quotes
                    return `"${stringField.replace(/"/g, '""')}"`;
                }
                return stringField;
            }).join(',')
        ).join('\n');

       // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);

       // Generate filename with current date
        const currentDate = new Date().toISOString().split('T')[0];
        const filename = `Practo_MIS_Report_${userType}_${empId || agentId || 'All'}_${currentDate}.csv`;
        link.setAttribute('download', filename);

        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleLogout = () => {
        try {
            localStorage.clear();
            sessionStorage.clear();
            logout();
            window.location.href = '/login';
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    const goBack = () => {
        navigate('/dashboard');
    };
    
    const goBackToPracto = () => {
        navigate('/Practo', { state: { agentData: state.agentData } });
    };

    const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) =>
        (e: React.ChangeEvent<HTMLInputElement>) => setter(e.target.value);

    // Determine which field to show based on user type
    const showEmployeeField = userType === 'Employee';
    const showAgentField = userType === 'Agent';
    const showAdminField = userType === 'Admin';

     return (
        <div className="coi-container">
            {/* Header */}
            <header className="coi-header">
                <div className="coi-header-content">
                    <img src={logo} alt="ZextrA Travel Assist" className="logo-image" style={{ maxHeight: '60px', width: 'auto' }} />
                    <div className="d-flex justify-content-center py-4">
                        <div className="logo d-flex align-items-center w-auto">
                            <span className="page-title">Practo Subscription</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '20px' }}>
                        <button
                            onClick={goBack}
                            className="coi-button"
                        >
                            <Home size={18} />
                            Dashboard
                        </button>
                        <button
                            onClick={handleLogout}
                            className="coi-button coi-logout-button"
                        >
                            <LogOut size={18} />
                            Logout
                        </button>
                    </div>
                </div>
            </header>
            <main className="coi-main-content">
                 <div style={{ marginBottom: '15px', display: 'flex', width: '100%' }}>
                    <button
                        onClick={goBackToPracto}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.6rem 1.2rem',
                            backgroundColor: '#e0e7ff',
                            color: '#3b82f6',
                            border: '1px solid #bfdbfe',
                            borderRadius: '0.375rem',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#bfdbfe';
                            e.currentTarget.style.color = '#2563eb';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = '#e0e7ff';
                            e.currentTarget.style.color = '#3b82f6';
                        }}
                    >
                        <ArrowLeftCircle size={18} />
                        <span>Back To Practo</span>
                    </button>
                </div>
                <div className="coi-card">
                    <div className="coi-card-header">
                        <h2 className="coi-card-title">{userType} MIS Reports Details</h2>
                        {empId && userType === 'Employee' && (
                            <p style={{ margin: '10px 0', color: '#6b7280', fontSize: '14px' }}>
                                Employee ID: <strong>{empId}</strong>
                            </p>
                        )}
                        {agentId && userType === 'Agent' && (
                            <p style={{ margin: '10px 0', color: '#6b7280', fontSize: '14px' }}>
                                Agent ID: <strong>{agentId}</strong>
                            </p>
                        )}
                        {adminId && userType === 'Admin' && (
                            <p style={{ margin: '10px 0', color: '#6b7280', fontSize: '14px' }}>
                                Admin ID: <strong>{adminId}</strong>
                            </p>
                        )}
                    </div>

                    {/* Search Form */}
                    <div className="coi-filter-container">
                        <div style={{
                            display: 'flex',
                            gap: '15px',
                            alignItems: 'end',
                            flexWrap: 'wrap',
                            marginBottom: '15px',
                            padding: '10px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px',
                            marginRight: '5px',
                            border: '1px solid #e2e8f0'
                        }}>
                            {showEmployeeField && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                                        Employee ID: {userType === 'Employee' && <span style={{ color: '#ef4444' }}>*</span>}
                                    </label>
                                    <input
                                        type="text"
                                        value={empId}
                                        onChange={(e) => setEmpId(e.target.value)}
                                        placeholder="Enter Employee ID"
                                        className="coi-filter-input"
                                        style={{
                                            width: '150px',
                                            backgroundColor: (location.state?.empid && userType === 'Employee') ? '#f3f4f6' : 'white'
                                        }}
                                        readOnly={location.state?.empid && userType === 'Employee'}
                                    />
                                </div>
                            )}

                            {showAgentField && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                                        Agent ID: {userType === 'Agent' && <span style={{ color: '#ef4444' }}>*</span>}
                                    </label>
                                    <input
                                        type="text"
                                        value={agentId}
                                        onChange={(e) => setAgentId(e.target.value)}
                                        placeholder="Enter Agent ID"
                                        className="coi-filter-input"
                                        style={{
                                            width: '150px',
                                            backgroundColor: (location.state?.agent && userType === 'Agent') ? '#f3f4f6' : 'white'
                                        }}
                                        readOnly={location.state?.agent && userType === 'Agent'}
                                    />
                                </div>
                            )}

                            {showAdminField && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                                        Admin ID:
                                    </label>
                                    <input
                                        type="text"
                                        value={adminId}
                                        onChange={(e) => setAdminId(e.target.value)}
                                        placeholder="Enter Admin ID (Optional)"
                                        className="coi-filter-input"
                                        style={{ width: '150px' }}
                                        readOnly={location.state?.adminId && userType === 'Admin'}
                                    />
                                </div>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                                    From Date: <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="coi-filter-input"
                                    style={{ width: '150px' }}
                                />
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                                    To Date: <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="coi-filter-input"
                                    style={{ width: '150px' }}
                                />
                            </div>

                            <div style={{ display: 'flex', alignItems: 'end' }}>
                                <button
                                    onClick={handleSearch}
                                    disabled={loading || !startDate || !endDate || (userType === 'Employee' && !empId) || (userType === 'Agent' && !agentId)}
                                    className="coi-search-button"
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: (!startDate || !endDate || (userType === 'Employee' && !empId) || (userType === 'Agent' && !agentId)) ? '#9ca3af' : '#3b82f6',
                                        cursor: (!startDate || !endDate || (userType === 'Employee' && !empId) || (userType === 'Agent' && !agentId)) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <Search size={16} style={{ marginRight: '5px' }} />
                                    {loading ? 'Searching...' : 'Search'}
                                </button>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'end' }}>
                                <button
                                    className='apply-btn-emp'
                                    onClick={downloadCSV}
                                    disabled={proposal.length === 0}
                                    style={{
                                        opacity: proposal.length === 0 ? 0.5 : 1,
                                        cursor: proposal.length === 0 ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px'
                                    }}
                                >
                                    <Download size={16} />
                                    Download CSV
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Results */}
                    {loading && <p>Loading...</p>}

                    {error && <p style={{ color: 'red' }}>{error}</p>}

                    {proposal.length > 0 && (
                        <div>
                            <p><strong>{proposal.length} proposals found</strong></p>

                            <div className="coi-table-container">
                                <table className="coi-table">
                                    <thead>
                                        <tr>
                                            <th className="coi-table-header">Sr.No</th>
                                            <th className="coi-table-header">Practo Proposal ID</th>
                                            <th className="coi-table-header">Practo Invoice No</th>
                                            <th className="coi-table-header">Plan Name</th>
                                            <th className="coi-table-header">Name of the Passenger</th>
                                            <th className="coi-table-header">Passenger Mobile No</th>
                                         
                                            <th className="coi-table-header">Policy Subscription Date</th>
                                            <th className="coi-table-header">Pre Tax Amount</th>
                                            <th className="coi-table-header">Post Tax Amount</th>
                                            <th className="coi-table-header">Agent ID</th>
                                            <th className="coi-table-header">Agent Name</th>
                                            <th className="coi-table-header">Agent Mobile No</th>
                                            <th className="coi-table-header">Selected Payment Mode</th>
                                            <th className="coi-table-header">Payment Type</th>
                                            <th className="coi-table-header">Discount/Comm%</th>
                                            <th className="coi-table-header">
                                                {paymentFilter === 'Upfront' ? 'Upfront Amount Paid' : 'Full Pay/Discount Amount Paid'}
                                            </th>
                                            <th className="coi-table-header">Payment Received</th>
                                            <th className="coi-table-header">Practo Fees</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {proposal.map((proposal: Proposal, index: number) => (
                                            <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                                                <td className="coi-table-cell">{index + 1}</td>
                                                <td className="coi-table-cell">{proposal.Practo_proposal_id || ''}</td>
                                                <td className="coi-table-cell">{proposal.Practo_inv_number || ''}</td>
                                                <td className="coi-table-cell">{proposal.Planname || ''}</td>
                                                <td className="coi-table-cell">{proposal.Nameofthepassenger || ''}</td>
                                                <td className="coi-table-cell">{proposal.passenger_Mobileno || ''}</td>
                                               
                                                <td className="coi-table-cell">{formatDate(proposal.Policy_Generation_Date)}</td>
                                                <td className="coi-table-cell" style={{ textAlign: "center", verticalAlign: "middle" }}>{proposal.Assiatance_charges_PreTaxAmount || ''}</td>
                                                <td className="coi-table-cell" style={{ textAlign: "center", verticalAlign: "middle" }}>{proposal.Assiatance_charges_PostTaxAmount || ''}</td>
                                                <td className="coi-table-cell">{proposal.AgentId || ''}</td>
                                                <td className="coi-table-cell">{proposal.AgentName || ''}</td>
                                                <td className="coi-table-cell">{proposal.Agent_Mobileno || ''}</td>
                                                <td className="coi-table-cell">{proposal.Selected_Payment_Mode || ''}</td>
                                                <td className="coi-table-cell">{proposal.PaymentType || ''}</td>
                                                <td className="coi-table-cell" style={{ textAlign: "center", verticalAlign: "middle" }}>{proposal.Discount || ''}</td>
                                                <td className="coi-table-cell" style={{ textAlign: "center", verticalAlign: "middle" }}>
                                                    {proposal.Selected_Payment_Mode === 'Upfront'
                                                        ? calculateUpfrontAmount(proposal)
                                                        : proposal.Fullpay_Discount_amount_to_be_paid
                                                    }
                                                </td>
                                                <td className="coi-table-cell">
                                                    <span style={{
                                                        padding: '2px 6px',
                                                        borderRadius: '3px',
                                                        backgroundColor: proposal.Paymentreceived === 'Payment received' ? '#d4edda' : '#fff3cd',
                                                        color: proposal.Paymentreceived === 'Payment received' ? '#155724' : '#856404'
                                                    }}>
                                                        {proposal.Paymentreceived || ''}
                                                    </span>
                                                </td>
                                                <td className="coi-table-cell">{proposal.practo_fees || ''}</td>    
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </main>
            <footer className="coi-footer">
                <p>© {new Date().getFullYear()} Interstellar Services Pvt. Ltd., All rights reserved</p>
            </footer>
        </div>
    );
};

export default MIS_Proposal_Practo;